import { movies as rawMovies } from "@/mocks/movies";
import { news as rawNews } from "@/mocks/news";
import { specials as rawSpecials } from "@/mocks/specials";
import type { Movie, NewsArticle, Special } from "@/types/content";

export const movies = rawMovies as unknown as Movie[];
export const newsList = rawNews as unknown as NewsArticle[];
export const specials = rawSpecials as unknown as Special[];

export const genreOptions = ["全部", "剧情", "爱情", "悬疑", "古装", "犯罪", "年代", "都市", "奇幻", "文艺"];

export const regionOptions = ["全部", "中国大陆", "中国香港", "中国台湾", "韩国", "日本", "英国"];

export const yearOptions = ["全部", "2025", "2024", "2023"];

export const newsCategoryOptions = ["全部", "影视新闻", "幕后揭秘", "发行动态"];

export const sortOptions = [
  { value: "release", label: "按上映时间" },
  { value: "rating", label: "按评分" },
  { value: "heat", label: "按热度" },
] as const;

export type SortValue = (typeof sortOptions)[number]["value"];

export function getMovieById(id?: string) {
  return movies.find((movie) => movie.id === id);
}

export function getNewsById(id?: string) {
  return newsList.find((article) => article.id === id);
}

export function getSpecialById(id?: string) {
  return specials.find((item) => item.id === id);
}

export function getRelatedMovies(movie: Movie, limit = 4) {
  const others = movies.filter((item) => item.id !== movie.id);
  const scored = others.map((item) => {
    const sharedGenres = item.genres.filter((genre) => movie.genres.includes(genre)).length;
    const sharedTags = item.tags.filter((tag) => movie.tags.includes(tag)).length;
    const sameRegion = item.region === movie.region ? 1 : 0;
    return { item, score: sharedGenres * 3 + sharedTags * 2 + sameRegion };
  });
  return scored
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function getRelatedNews(article: NewsArticle, limit = 3) {
  const mapped = article.relatedIds
    .map((id) => newsList.find((item) => item.id === id))
    .filter((item): item is NewsArticle => Boolean(item));
  if (mapped.length >= limit) return mapped.slice(0, limit);
  const extra = newsList.filter(
    (item) => item.id !== article.id && !mapped.some((entry) => entry.id === item.id) && item.category === article.category,
  );
  return [...mapped, ...extra].slice(0, limit);
}

export function filterMovies(params: {
  genre?: string;
  year?: string;
  region?: string;
  sort?: SortValue;
}) {
  const { genre = "全部", year = "全部", region = "全部", sort = "release" } = params;
  const result = movies.filter((movie) => {
    const matchGenre = genre === "全部" || movie.genres.includes(genre);
    const matchYear = year === "全部" || movie.year === Number(year);
    const matchRegion = region === "全部" || movie.region === region;
    return matchGenre && matchYear && matchRegion;
  });
  return result.sort((a, b) => {
    if (sort === "rating") return b.rating - a.rating;
    if (sort === "heat") return b.heat - a.heat;
    return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
  });
}

export function searchContent(keyword: string) {
  const query = keyword.trim().toLowerCase();
  if (!query) {
    return { movieResults: [] as Movie[], newsResults: [] as NewsArticle[] };
  }
  const movieResults = movies.filter((movie) =>
    [movie.title, movie.enTitle, movie.summary, movie.tagline, movie.director, ...movie.genres, ...movie.cast, ...movie.tags]
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
  const newsResults = newsList.filter((article) =>
    [article.title, article.summary, article.category, ...article.content]
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
  return { movieResults, newsResults };
}

export const topRatedMovies = [...movies].sort((a, b) => b.rating - a.rating).slice(0, 3);